import React, { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { api } from "../Assets/assetsShared";
import "./EmployeeSidebar.css";

/* =========================================================
   MENU  (permission = [pageKey, action] from Role & Permission)
   section = small heading shown above the item
========================================================= */

const MENU = [
  {
    id: "dashboard",
    section: "Main",
    label: "Dashboard",
    icon: "🏠",
    to: "/admin",
    permission: ["dashboard", "view"],
  },
  {
    id: "tool",
    section: "Workspace",
    label: "Tool",
    icon: "🛠️",
    items: [
      { label: "Notes", icon: "📝", to: "/tools/notes", permission: ["notes", "view"] },
    ],
  },
  {
    id: "messenger",
    section: "Workspace",
    label: "Messenger",
    icon: "💬",
    items: [
      {
        label: "User wise",
        icon: "👤",
        to: "/messenger",
        end: true,
        permission: ["messenger_users", "view"],
      },
      {
        label: "Project-wise",
        icon: "📁",
        to: "/messenger/project-communication",
        permission: ["messenger_project", "view"],
      },
    ],
  },
  {
    id: "email",
    section: "Workspace",
    label: "Email",
    icon: "✉️",
    items: [
      {
        label: "Email Box",
        icon: "📥",
        to: "/email",
        permission: ["email_box", "view"],
      },
    ],
  },
  {
    id: "hr",
    section: "People",
    label: "HR",
    icon: "👥",
    items: [
      {
        label: "Timesheet",
        icon: "⏱️",
        to: "/timesheet",
        permission: ["timesheet", "view"],
      },
      {
        label: "Employee Log Detail",
        icon: "📝",
        to: "/employee-log",
        permission: ["employee_log", "view"],
      },
      {
        label: "Leave Application",
        icon: "🏖️",
        to: "/leave-application",
        permission: ["leave_application", "view"],
      },
      {
        label: "Leave Management",
        icon: "✅",
        to: "/leave-management",
        permission: ["leave_management", "view"],
      },
      {
        label: "Employee Review",
        icon: "⭐",
        to: "/employee-reviews",
        permission: ["employee_review", "view"],
      },
      {
        label: "Create Email",
        icon: "📧",
        to: "/hr/create-mail-account",
        permission: ["create_email", "view"],
      },
      {
        label: "Manage Employee",
        icon: "👥",
        to: "/manage-employees",
        permission: ["manage_employee", "view"],
      },
      {
        label: "Role & Permission",
        icon: "🔐",
        to: "/role-permissions",
        permission: ["role_permission", "view"],
      },
    ],
  },
  {
    id: "hiring",
    section: "People",
    label: "Hiring Management",
    icon: "🧑‍💼",
    items: [
      { label: "Hiring Dashboard", icon: "📊", to: "/hr-dashboard", permission: ["hiring_dashboard", "view"] },
      { label: "Job Openings", icon: "💼", to: "/hr/job-openings", permission: ["job_openings", "view"] },
      { label: "Candidates", icon: "👤", to: "/hr/candidates", permission: ["candidates", "view"] },
      { label: "Interviews", icon: "🗓️", to: "/hr/interviews", permission: ["interviews", "view"] },
      { label: "Offers", icon: "📄", to: "/hr/offers", permission: ["offers", "view"] },
      { label: "Upcoming Joinings", icon: "🤝", to: "/hr/joining", permission: ["joining", "view"] },
      { label: "Closed Hiring", icon: "✅", to: "/hr/closed-hiring", permission: ["closed_hiring", "view"] },
      { label: "Recruitment Reports", icon: "📈", to: "/hr/recruitment-reports", permission: ["recruitment_reports", "view"] },
    ],
  },
  {
    id: "admin",
    section: "Admin",
    label: "Admin & Internal Management",
    icon: "🏢",
    sectionLabel: "Asset",
    items: [
      { label: "My Hardware", icon: "🖥️", to: "/my-hardware", permission: ["hardware_employee", "view"] },
      { label: "Software All Staff", icon: "📦", to: "/software", permission: ["software_all", "view"] },
      {
        label: "Hardware All Staff",
        icon: "🗄️",
        to: "/hardware",
        permission: ["hardware_all", "view"],
        extraPaths: ["/hardware-destroyed"],
      },
    ],
  },
];

const FALLBACK_PERMISSIONS = { dashboard: ["view"] };

const itemMatches = (item, path) =>
  [item.to, ...(item.extraPaths || [])].some((base) =>
    item.end ? path === base : path === base || path.startsWith(`${base}/`)
  );

export default function EmployeeSidebar() {
  const location = useLocation();
  const [permissions, setPermissions] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [openGroup, setOpenGroup] = useState("");

  /* ---------- load my permissions ---------- */
  useEffect(() => {
    let cancelled = false;

    api
      .get("/role-permissions/me")
      .then((res) => {
        if (cancelled) return;
        setPermissions(res.data?.permissions || {});
        setIsAdmin(Boolean(res.data?.isAdmin));
      })
      .catch(() => {
        if (!cancelled) setPermissions(FALLBACK_PERMISSIONS);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  /* ---------- open the group of the current page ---------- */
  useEffect(() => {
    const current = MENU.find((group) =>
      group.items?.some((item) => itemMatches(item, location.pathname))
    );

    if (current) setOpenGroup(current.id);
  }, [location.pathname]);

  const has = (permission) =>
    !permission || (permissions?.[permission[0]] || []).includes(permission[1]);

  /* ---------- only what this role can see ---------- */
  const visibleMenu = (permissions ? MENU : [])
    .map((entry) =>
      entry.items
        ? { ...entry, visibleItems: entry.items.filter((item) => has(item.permission)) }
        : entry
    )
    .filter((entry) => (entry.items ? entry.visibleItems.length : has(entry.permission)));

  let lastSection = "";

  return (
    <nav className="es-sidebar">
      {/* ----- brand ----- */}
      <div className="es-brand">
        <div className="es-brand-logo">S</div>

        <div className="es-brand-text">
          <strong>Softvolute ERP</strong>
          <span>{isAdmin ? "Admin Menu" : "Employee Menu"}</span>
        </div>
      </div>

      {permissions === null ? (
        <div className="es-loading">Loading menu...</div>
      ) : (
        <ul className="es-list">
          {visibleMenu.map((entry) => {
            const showSection = entry.section !== lastSection;
            lastSection = entry.section;

            const sectionHeading = showSection && (
              <li className="es-section-title">{entry.section}</li>
            );

            /* ----- single link ----- */
            if (!entry.items) {
              return (
                <React.Fragment key={entry.id}>
                  {sectionHeading}

                  <li>
                    <NavLink
                      to={entry.to}
                      end
                      className={({ isActive }) =>
                        isActive ? "es-main es-main-active" : "es-main"
                      }
                    >
                      <span className="es-left">
                        <span className="es-icon">{entry.icon}</span>
                        <span className="es-label">{entry.label}</span>
                      </span>
                    </NavLink>
                  </li>
                </React.Fragment>
              );
            }

            /* ----- group with sub links ----- */
            const isOpen = openGroup === entry.id;
            const hasActiveChild = entry.visibleItems.some((item) =>
              itemMatches(item, location.pathname)
            );

            return (
              <React.Fragment key={entry.id}>
                {sectionHeading}

                <li>
                  <button
                    type="button"
                    className={`es-main es-group ${hasActiveChild ? "es-has-active" : ""}`}
                    onClick={() => setOpenGroup(isOpen ? "" : entry.id)}
                  >
                    <span className="es-left">
                      <span className="es-icon">{entry.icon}</span>
                      <span className="es-label">{entry.label}</span>
                    </span>

                    <span className={`es-arrow ${isOpen ? "open" : ""}`}>▾</span>
                  </button>

                  {isOpen && (
                    <ul className="es-sub">
                      {entry.sectionLabel && (
                        <li className="es-sub-heading">{entry.sectionLabel}</li>
                      )}

                      {entry.visibleItems.map((item) => (
                        <li key={item.to}>
                          <NavLink
                            to={item.to}
                            className={() =>
                              itemMatches(item, location.pathname)
                                ? "es-sub-link es-sub-active"
                                : "es-sub-link"
                            }
                          >
                            <span className="es-sub-icon">{item.icon}</span>
                            <span>{item.label}</span>
                          </NavLink>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              </React.Fragment>
            );
          })}
        </ul>
      )}
    </nav>
  );
}